/**
 * i18n - 簡化的多語言配置
 * 
 * 設計原則：
 * 1. 語系檔放在 public/locales/{namespace}/{lang}.json
 * 2. 直接作為靜態檔案存取，不需要 API
 * 3. 按需載入 namespace，減少初始載入時間
 * 4. 對於 templates 中的多語言物件，使用 getText 函數處理
 * 
 * 使用方式:
 * import { useTranslation } from 'react-i18next';
 * const { t } = useTranslation(['common', 'home']);
 * 
 * 或使用 useText hook 處理多語言物件:
 * import { useText } from '@/hooks';
 * const getText = useText();
 * getText({ en: 'Name', 'zh-TW': '姓名' });
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';

// ========== 配置 ==========
export const LANGUAGES = [
    { code: 'en', name: 'English' },
    { code: 'zh-CN', name: '简体中文' },
    { code: 'zh-TW', name: '繁體中文' },
];

export const DEFAULT_LANG = 'en';
export const STORAGE_KEY = 'oa-language';

// 別名導出 (向後相容)
export const supportedLanguages = LANGUAGES;
export const defaultLanguage = DEFAULT_LANG;
export const LANGUAGE_STORAGE_KEY = STORAGE_KEY;

// ========== 初始化 ==========
i18n
    .use(HttpBackend)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
        fallbackLng: DEFAULT_LANG,
        supportedLngs: LANGUAGES.map(l => l.code),
        load: 'currentOnly',
        debug: false,
        
        // 預設載入的 namespaces
        ns: ['common', 'forms', 'admin'],
        defaultNS: 'common',
        
        // 語言偵測設定
        detection: {
            order: ['localStorage', 'navigator'],
            lookupLocalStorage: STORAGE_KEY,
            caches: ['localStorage'],
            convertDetectedLanguage: (lng) => {
                if (lng?.startsWith('zh-TW') || lng?.startsWith('zh-Hant') || lng === 'zh-Hant') return 'zh-TW';
                if (lng?.startsWith('zh')) return 'zh-CN';
                if (lng?.startsWith('en')) return 'en';
                return DEFAULT_LANG;
            },
        },
        
        // 後端設定 - 直接從 public/locales 讀取靜態檔案
        backend: {
            loadPath: '/locales/{{ns}}/{{lng}}.json',
        },
        
        react: { 
            useSuspense: false,
        },
        
        interpolation: { 
            escapeValue: false,
        },
    });

// ========== 工具函數 ==========

/**
 * 取得當前語言
 */
export const getLanguage = () => {
    return i18n.language || localStorage.getItem(STORAGE_KEY) || DEFAULT_LANG;
};

// 別名
export const getCurrentLanguage = getLanguage;

/**
 * 切換語言
 */
export const setLanguage = async (code) => {
    await i18n.changeLanguage(code);
    localStorage.setItem(STORAGE_KEY, code);
    document.documentElement.lang = code;
};

// 別名
export const changeLanguage = setLanguage;

/**
 * 從多語言物件取得文字
 * 支援物件格式 { en: '...', 'zh-TW': '...' }
 * 
 * @param {string|object} obj - 字串或多語言物件
 * @param {string} fallback - 備用值
 * @returns {string}
 */
export const getText = (obj, fallback = '') => {
    if (!obj) return fallback;
    if (typeof obj === 'string') return obj;
    if (typeof obj !== 'object') return fallback;
    
    const lang = getLanguage();
    // 優先順序: 當前語言 -> 語言簡碼 -> 英文 -> 第一個值 -> fallback
    return obj[lang] 
        || obj[lang.split('-')[0]] 
        || obj['en'] 
        || Object.values(obj)[0] 
        || fallback;
};

// 別名 (向後相容)
export const getLocalizedText = getText;

/**
 * 載入額外的 namespace
 * 用於按需載入頁面特定的語系
 * 
 * @param {string|string[]} namespaces - 要載入的 namespace
 * @returns {Promise<void>}
 */
export const loadNamespaces = async (namespaces) => {
    const ns = Array.isArray(namespaces) ? namespaces : [namespaces];
    await i18n.loadNamespaces(ns);
};

/**
 * 載入表單翻譯（向後相容）
 * @deprecated 使用 getText 處理 templates 中的多語言物件
 */
export const loadFormTranslations = async (formId) => {
    // 表單翻譯現在內嵌在 form.json/route.json 中
    // 不需要額外載入，直接使用 getText 處理
    return null;
};

/**
 * 重新載入翻譯
 */
export const reloadTranslations = async () => {
    await i18n.reloadResources();
};

export default i18n;
